// Kompakte Anlagen-Links (z. B. unter Beschreibung) — öffnet FilePreviewModal
import React, { useCallback, useEffect, useState } from "react";
import { Paperclip } from "lucide-react";
import api from "../../constants/api.js";
import FilePreviewModal from "./FilePreviewModal.jsx";
import {
  getFileExtension,
  sanitizeFileUrl,
  shouldUsePreviewMeta,
} from "../../utils/filePreview.js";
import { resolveEntityFileUrl } from "../../utils/entityFileUrl.js";
import { resolveEntityInlineFileIcon } from "../../utils/entityInlineFileIcon.js";

function parseFileList(data) {
  if (Array.isArray(data)) return data;
  return data?.items || data?.dateien || data?.rows || data?.uploads || [];
}

function scrollToDocuments(targetId) {
  if (!targetId) return;
  const el = document.getElementById(targetId);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function fileLinkLabel(row) {
  const typed = [row?.typ, row?.beschreibung].filter(Boolean).join(" · ");
  if (typed) return typed;
  return row?.originalname || "Dokument";
}

export default function EntityInlineFileLinks({
  listUrl,
  resolveFileUrl = null,
  previewModule = "",
  entityId = null,
  projektTyp = "",
  projektId = null,
  label = "Anlagen",
  className = "",
  maxVisible = 2,
  scrollTargetId = "",
  documentsSectionLabel = "Dokumente",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState({
    open: false,
    url: "",
    name: "",
    type: "",
    id: null,
  });

  const loadItems = useCallback(async () => {
    if (!listUrl) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const res = await api.get(listUrl, { silent: true });
      const rows = parseFileList(res.data);
      setItems(Array.isArray(rows) ? rows : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [listUrl]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const resolveUrl = useCallback(
    (row) => {
      if (typeof resolveFileUrl === "function") return resolveFileUrl(row);
      return resolveEntityFileUrl(row);
    },
    [resolveFileUrl]
  );

  const openPreview = (row) => {
    const rawUrl = resolveUrl(row);
    const url = sanitizeFileUrl(rawUrl);
    const fileId = row?.id ?? null;
    const canPreview = Boolean(url || shouldUsePreviewMeta({ previewModule, fileId }));
    if (!canPreview) return;
    setPreview({
      open: true,
      url: url || sanitizeFileUrl(rawUrl),
      name: row?.originalname || fileLinkLabel(row),
      type: getFileExtension(row?.originalname || row?.dateipfad),
      id: fileId,
    });
  };

  if (loading || items.length === 0) return null;

  const limit = Math.max(1, Number(maxVisible) || 2);
  const visibleItems = items.slice(0, limit);
  const hiddenCount = Math.max(0, items.length - visibleItems.length);

  return (
    <>
      <div className={`mt-2 pt-2 border-t border-white/10 ${className}`.trim()}>
        <div className="text-xs uppercase tracking-wide text-blue-300/60 mb-1.5 flex items-center gap-1.5">
          <Paperclip className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
          {label}
          <span className="normal-case tracking-normal text-blue-200/45">({items.length})</span>
        </div>
        <ul className="space-y-0.5">
          {visibleItems.map((row) => {
            const key = row?.id ?? row?.originalname;
            const title = fileLinkLabel(row);
            const fileName = String(row?.originalname || "").trim();
            const showFileName = fileName && !title.includes(fileName);
            const FileIcon = resolveEntityInlineFileIcon(row);
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => openPreview(row)}
                  className="group flex w-full items-start gap-2 rounded-md px-1.5 py-1 -mx-1.5 text-left transition-colors hover:bg-white/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400/50"
                >
                  <FileIcon
                    className="mt-0.5 h-4 w-4 shrink-0 text-blue-300/70 group-hover:text-blue-200"
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="line-clamp-2 text-sm text-blue-100/90 underline-offset-2 group-hover:underline">
                      {title}
                    </span>
                    {showFileName ? (
                      <span className="block truncate text-[11px] text-blue-200/55">{fileName}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {hiddenCount > 0 ? (
          <button
            type="button"
            onClick={() => scrollToDocuments(scrollTargetId)}
            className="mt-1 px-1.5 -mx-1.5 text-left text-[12px] text-blue-300/65 underline-offset-2 hover:text-blue-200/90 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-400/50 rounded"
          >
            und {hiddenCount} weitere — siehe {documentsSectionLabel} unten
          </button>
        ) : null}
      </div>

      <FilePreviewModal
        open={preview.open}
        onClose={() => setPreview((prev) => ({ ...prev, open: false }))}
        fileUrl={preview.url}
        fileName={preview.name}
        fileType={preview.type}
        previewModule={previewModule}
        fileId={preview.id}
        projektTyp={projektTyp}
        projektId={projektId}
        entityId={entityId}
      />
    </>
  );
}
